<template>
  <v-dialog :value="value" persistent scrollable max-width="520px">
    <v-card class="ol-nomear">
      <!-- Mesmo cabeçalho do "Salvar Imagem" da visita técnica (VisitaPhoto.vue) -->
      <v-card-title class="headline green white--text ol-nomear__titulo">
        <span>{{ itens.length === 1 ? 'Salvar arquivo' : `Salvar ${itens.length} arquivos` }}</span>
        <v-icon large color="white">mdi-content-save</v-icon>
      </v-card-title>

      <v-card-text class="ol-nomear__lista">
        <p class="ol-nomear__msg">
          Digite um nome para {{ itens.length === 1 ? 'o arquivo' : 'cada arquivo' }}. Se deixar em branco, o nome é gerado automaticamente.
        </p>
        <div v-for="(item, i) in itens" :key="i" class="ol-nomear__item">
          <div class="ol-nomear__mini">
            <img v-if="item.tipo === 'imagem'" :src="item.url" alt="">
            <video v-else-if="item.tipo === 'video'" :src="`${item.url}#t=0.1`" muted playsinline preload="metadata"></video>
            <v-icon v-else large color="blue-grey">mdi-file-document-outline</v-icon>
            <span v-if="item.tipo === 'video' && item.duracao != null" class="ol-nomear__badge">
              ▶ {{ $$_formatarTempo(item.duracao) }}
            </span>
          </div>
          <div class="ol-nomear__campo">
            <v-text-field
              v-model="nomes[i]"
              label="Nome"
              :placeholder="$$_automaticos[i]"
              persistent-placeholder
              :suffix="`.${$$_extensoes[i]}`"
              :maxlength="limiteNome"
              counter
              :error-messages="$$_erros[i] ? [$$_erros[i]] : []"
              :hint="$$_dica(i)"
              persistent-hint
              outlined
              dense
              autocomplete="off"
              @keydown.enter="$_confirmar"
            ></v-text-field>
            <span class="ol-nomear__meta">{{ $$_formatarTamanho(item.file.size) }}</span>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="ol-nomear__acoes">
        <template v-if="confirmaDescarte">
          <span class="ol-nomear__pergunta">Descartar {{ itens.length === 1 ? 'o arquivo' : `os ${itens.length} arquivos` }}?</span>
          <span class="ol-nomear__espaco"></span>
          <v-btn text @click="confirmaDescarte = false">Não</v-btn>
          <v-btn color="red" class="white--text" depressed @click="$_descartar">Descartar</v-btn>
        </template>
        <template v-else>
          <span class="ol-nomear__espaco"></span>
          <v-btn text class="black--text" @click="confirmaDescarte = true">Cancelar</v-btn>
          <v-btn color="blue-grey" class="white--text" depressed @click="$_confirmar">Salvar</v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  LIMITE_NOME, extensaoPorMime, nomeAutomatico, sanitizarNome, semExtensao, formatarTamanho, formatarTempo,
} from '../../utils/midia';

export default {
  name: 'MidiaNomear',

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    // [{ file, tipo, duracao, origem, url }]
    itens: {
      type: Array,
      default: () => [],
    },
    // nomes completos (com extensão) que já estão na pergunta
    nomesExistentes: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      nomes: [],
      abertoEm: new Date(),
      confirmaDescarte: false,
      limiteNome: LIMITE_NOME,
    };
  },

  computed: {
    $$_extensoes() {
      return this.itens.map((it) => extensaoPorMime(it.file.type, it.file.name) || 'bin');
    },
    // Nome digitado, já limpo (sem acento e sem caracteres de caminho), ou ''.
    $$_digitados() {
      return this.itens.map((it, i) => {
        const base = sanitizarNome(this.nomes[i]);
        return base ? `${base}.${this.$$_extensoes[i]}` : '';
      });
    },
    // Nome usado quando o campo fica vazio: foto_/video_/arquivo_ + data e hora,
    // com _2, _3... se colidir com algo que já está na pergunta ou neste lote.
    $$_automaticos() {
      const usados = new Set(this.nomesExistentes.map((n) => String(n).toLowerCase()));
      this.$$_digitados.forEach((n) => { if (n) usados.add(n.toLowerCase()); });
      return this.itens.map((it, i) => {
        const ext = this.$$_extensoes[i];
        const nome = nomeAutomatico(it.tipo, ext, usados, this.abertoEm);
        // Só reserva o nome se ele for de fato usado (campo vazio).
        if (!this.$$_digitados[i]) usados.add(`${nome}.${ext}`.toLowerCase());
        return nome;
      });
    },
    $$_finais() {
      return this.$$_digitados.map((digitado, i) => digitado || `${this.$$_automaticos[i]}.${this.$$_extensoes[i]}`);
    },
    // Só nome digitado gera erro; o automático já sai único.
    $$_erros() {
      const existentes = this.nomesExistentes.map((n) => String(n).toLowerCase());
      const digitados = this.$$_digitados.map((n) => n.toLowerCase());
      return digitados.map((nome, i) => {
        if (!nome) return '';
        // A API grava em <form>/<questão>/<nome>: nome repetido sobrescreve o outro arquivo.
        if (existentes.includes(nome)) return 'Já existe um arquivo com esse nome nesta pergunta';
        if (digitados.indexOf(nome) !== i) return 'Nome repetido';
        return '';
      });
    },
    $$_valido() {
      return this.$$_erros.every((e) => !e);
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(aberto) {
        if (!aberto) return;
        this.abertoEm = new Date();
        this.confirmaDescarte = false;
        // Câmera começa vazio (vale o automático); galeria/documento sugere o nome original.
        this.nomes = this.itens.map((it) => (it.origem === 'camera' ? '' : sanitizarNome(semExtensao(it.file.name)).replace(/_/g, ' ')));
      },
    },
  },

  methods: {
    $$_formatarTamanho: formatarTamanho,
    $$_formatarTempo: formatarTempo,

    $$_dica(i) {
      if (!this.$$_digitados[i]) return `Será salvo como ${this.$$_finais[i]}`;
      const digitado = `${String(this.nomes[i] || '').trim()}.${this.$$_extensoes[i]}`;
      return this.$$_finais[i] !== digitado ? `Será salvo como ${this.$$_finais[i]}` : '';
    },

    $_confirmar() {
      if (!this.$$_valido) return;
      this.$emit('confirmar', this.itens.map((it, i) => ({ ...it, nome: this.$$_finais[i] })));
    },

    $_descartar() {
      this.confirmaDescarte = false;
      this.$emit('descartar');
    },
  },
};
</script>

<style scoped>
.ol-nomear__titulo {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
  word-break: normal;
  gap: 12px;
}

.ol-nomear__lista {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-top: 16px !important;
  max-height: 65vh;
}

.ol-nomear__msg {
  margin: 0;
  font-size: 15px;
  color: rgba(0, 0, 0, 0.7);
}

.ol-nomear__item {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.ol-nomear__mini {
  position: relative;
  flex: 0 0 72px;
  height: 72px;
  border: 2px solid #4caf50;
  border-radius: 8px;
  overflow: hidden;
  background: #eceff1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ol-nomear__mini img,
.ol-nomear__mini video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ol-nomear__badge {
  position: absolute;
  left: 3px;
  bottom: 3px;
  font-size: 10px;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
  padding: 0 4px;
  border-radius: 4px;
}

.ol-nomear__campo {
  flex: 1;
  min-width: 0;
}

.ol-nomear__meta {
  display: block;
  margin-top: 2px;
  font-size: 12px;
  color: #78909c;
}

.ol-nomear__acoes {
  gap: 4px;
  padding: 8px 16px 14px;
}

.ol-nomear__pergunta {
  font-size: 14px;
}

.ol-nomear__espaco {
  flex: 1;
}
</style>
